/**
 * Login / Kayıt / Şifre Sıfırlama ekranı — MainLayout DIŞINDA render edilir (App.tsx AuthGate).
 * Her kullanıcı kendi hesabıyla girer ve SADECE kendi domainlerini görür.
 * ÇEKİDÜZEN: window.prompt/alert diyalogları kaldırıldı — "şifremi unuttum" ve
 * e-postadan gelen ?reset=TOKEN akışı kartın İÇİNDE profesyonel görünümlerle işler.
 * Bot koruması: görünmez bal küpü alanı (sunucu doluysa yutar) + sunucu tarafı hız sınırı.
 */
import { useState, useEffect } from "react";
import { setAuth, type AuthUser } from "@/lib/auth";

const C = {
  bg: "#fafaf9", surface: "#ffffff", border: "#e7e7e4",
  text1: "#1d1d1b", text2: "#70706c", purple: "#e83e3c", blue: "#ec6461",
  danger: "#d92d20", green: "#12b76a",
} as const;

type Gorunum = "login" | "register" | "forgot" | "reset";

export default function Login({ onSuccess }: { onSuccess: (user: AuthUser) => void }) {
  const [mode, setMode] = useState<Gorunum>("login");
  // Kayıt açık mı? Canlıda kapalı (davetli sistem) — sunucudan öğrenilir ki
  // ileride .env'den açıldığında ön yüzü yeniden derlemek gerekmesin.
  const [signupOpen, setSignupOpen] = useState<boolean | null>(null);
  useEffect(() => {
    void fetch("/api/auth/config")
      .then((r) => r.json())
      .then((d: { signupOpen?: boolean }) => setSignupOpen(!!d.signupOpen))
      .catch(() => setSignupOpen(false)); // ulaşılamazsa güvenli taraf: kapalı göster
  }, []);

  // ŞİFRE SIFIRLAMA: e-postadaki bağlantı ?reset=TOKEN ile buraya düşer → kart içi görünüm
  const [resetToken, setResetToken] = useState<string | null>(null);
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("reset");
    if (!token) return;
    setResetToken(token);
    setMode("reset");
    window.history.replaceState(null, "", window.location.pathname);
  }, []);

  // AÇILIŞ SAYFASINDAN "Üye Ol": ?kayit=1 → kayıt açıksa doğrudan Başvur sekmesi açılır
  const [kayitNiyeti, setKayitNiyeti] = useState(() => new URLSearchParams(window.location.search).has("kayit"));
  useEffect(() => {
    if (!kayitNiyeti || signupOpen === null) return;
    if (signupOpen) setMode("register");
    else setError("Kayıt şu anda kapalı — erişim için info@geolop.com adresinden bize ulaşın.");
    setKayitNiyeti(false);
    window.history.replaceState(null, "", window.location.pathname);
  }, [signupOpen, kayitNiyeti]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [name, setName] = useState("");
  const [balKupu, setBalKupu] = useState(""); // görünmez alan — insan asla doldurmaz
  const [error, setError] = useState<string | null>(null);
  const [basari, setBasari] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Google ile giriş denendi ama hesap tanımlı değil → sunucu ?kayitKapali=1 ile döner
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has("kayitKapali")) return;
    setError("Bu Google hesabı Geolop'ta tanımlı değil. Erişim için info@geolop.com adresinden bize ulaşın.");
    window.history.replaceState(null, "", window.location.pathname);
  }, []);
  // Başvuru onay bekliyor (Google ya da form kaydı sonrası girişte)
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has("onayBekliyor")) return;
    setBasari("Başvurun alındı ✋ — hesabın yönetici onayından sonra aktifleşecek. Onaylanınca e-posta ile haber vereceğiz.");
    window.history.replaceState(null, "", window.location.pathname);
  }, []);

  const gecis = (m: Gorunum) => { setMode(m); setError(null); setBasari(null); };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBasari(null);
    setBusy(true);
    try {
      if (mode === "forgot") {
        const r = await fetch("/api/auth/forgot", {
          method: "POST", headers: { "content-type": "application/json" },
          body: JSON.stringify({ email, web: balKupu }),
        });
        const d = await r.json().catch(() => ({} as { error?: string }));
        if (!r.ok) { setError(d.error ?? "İşlem başarısız"); return; }
        setBasari("Hesap varsa sıfırlama bağlantısı e-postana gönderildi — bağlantı 1 saat geçerli. Gelen kutunu (ve spam klasörünü) kontrol et.");
        return;
      }
      if (mode === "reset") {
        if (password !== password2) { setError("Parolalar birbirini tutmuyor."); return; }
        const r = await fetch("/api/auth/reset", {
          method: "POST", headers: { "content-type": "application/json" },
          body: JSON.stringify({ token: resetToken, password, web: balKupu }),
        });
        const d = await r.json().catch(() => ({} as { error?: string }));
        if (!r.ok) { setError(d.error ?? "Sıfırlama başarısız."); return; }
        setPassword(""); setPassword2("");
        gecis("login");
        setBasari("Parolan güncellendi ✓ — şimdi yeni parolanla giriş yapabilirsin.");
        return;
      }
      const body = mode === "login" ? { email, password, web: balKupu } : { email, password, name, web: balKupu };
      const r = await fetch(`/api/auth/${mode}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { setError(data.error ?? "İşlem başarısız"); return; }
      // Halka açık kayıt onay kapılı: sunucu oturum yerine "başvuru alındı" döner
      if (data.pending) {
        setPassword("");
        gecis("login");
        setBasari(data.message ?? "Başvurun alındı — onaylanınca haber vereceğiz.");
        return;
      }
      setAuth(data.token, data.user);
      onSuccess(data.user);
    } catch {
      setError("Sunucuya ulaşılamadı — bağlantını kontrol edip tekrar dene.");
    } finally {
      setBusy(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "10px 12px", borderRadius: 8,
    border: `1px solid ${C.border}`, background: "#fff",
    fontSize: 14, color: C.text1, outline: "none",
  };
  const etiket: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: C.text2, display: "block", marginBottom: 6 };

  return (
    <div style={{
      minHeight: "100vh", background: C.bg,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: 16,
    }}>
      <div style={{
        width: "100%", maxWidth: 400, background: C.surface,
        border: `1px solid ${C.border}`, borderRadius: 16,
        padding: "clamp(20px, 6vw, 32px)", boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
      }}>
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 8 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: `${C.purple}20`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src="/geolop-icon.svg" alt="" style={{ width: 40, height: 40 }} />
          </div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", color: C.text1 }}>
            <img src="/geolop-logo.svg" alt="Geolop" style={{ height: 34 }} />
          </span>
        </div>
        <p style={{ textAlign: "center", fontSize: 13, color: C.text2, marginBottom: 24 }}>
          {mode === "forgot" ? "Parolanı sıfırlayalım"
            : mode === "reset" ? "Yeni parolanı belirle"
            : "SEO · GEO · AEO çalışma alanınıza giriş yapın"}
        </p>

        {/* Giriş / Kayıt sekmeleri — kayıt kapalıysa ya da şifre akışındaysak çizilmez */}
        {signupOpen === true && (mode === "login" || mode === "register") && (
        <div style={{ display: "flex", background: "#f4f4f2", borderRadius: 10, padding: 4, marginBottom: 20 }}>
          {([["login", "Giriş Yap"], ["register", "Başvur"]] as const).map(([m, label]) => (
            <button key={m} type="button" onClick={() => gecis(m)}
              style={{
                flex: 1, padding: "8px 0", borderRadius: 8, border: "none", cursor: "pointer",
                fontSize: 13, fontWeight: 600,
                background: mode === m ? "#fff" : "transparent",
                color: mode === m ? C.text1 : C.text2,
                boxShadow: mode === m ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                transition: "all 0.15s",
              }}>
              {label}
            </button>
          ))}
        </div>
        )}

        {/* Google ile Giriş — yalnız giriş/kayıt görünümlerinde */}
        {(mode === "login" || mode === "register") && (<>
        <button type="button"
          onClick={() => { window.location.href = "/api/auth/google"; }}
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            padding: "10px 0", borderRadius: 10, border: `1px solid ${C.border}`, background: "#fff",
            cursor: "pointer", fontSize: 14, fontWeight: 600, color: C.text1, marginBottom: 16,
          }}>
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Google ile Giriş Yap
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, height: 1, background: C.border }} />
          <span style={{ fontSize: 11, color: C.text2 }}>veya e-posta ile</span>
          <div style={{ flex: 1, height: 1, background: C.border }} />
        </div>
        </>)}

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* BAL KÜPÜ — ekranda görünmez; botlar doldurur, sunucu dolu gelirse isteği yutar */}
          <input type="text" value={balKupu} onChange={(e) => setBalKupu(e.target.value)}
            name="web" tabIndex={-1} autoComplete="off" aria-hidden="true"
            style={{ position: "absolute", left: -9999, width: 1, height: 1, opacity: 0 }} />

          {mode === "register" && (
            <div>
              <label style={etiket}>Ad Soyad</label>
              <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="Tyler Durden" autoComplete="name" />
            </div>
          )}

          {mode !== "reset" && (
            <div>
              <label style={etiket}>E-posta</label>
              <input style={inputStyle} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ornek@mail.com" autoComplete="email" />
            </div>
          )}

          {(mode === "login" || mode === "register") && (
            <div>
              <label style={etiket}>Parola</label>
              <input style={inputStyle} type="password" required minLength={mode === "register" ? 10 : 6} value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "register" ? "En az 10 karakter" : "••••••••"}
                autoComplete={mode === "register" ? "new-password" : "current-password"} />
            </div>
          )}

          {mode === "reset" && (<>
            <div>
              <label style={etiket}>Yeni parola</label>
              <input style={inputStyle} type="password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="En az 10 karakter" autoComplete="new-password" autoFocus />
            </div>
            <div>
              <label style={etiket}>Yeni parola (tekrar)</label>
              <input style={inputStyle} type="password" required minLength={10} value={password2} onChange={(e) => setPassword2(e.target.value)}
                placeholder="Aynı parolayı tekrar yaz" autoComplete="new-password" />
            </div>
            <p style={{ fontSize: 11.5, color: C.text2, margin: 0, lineHeight: 1.5 }}>
              İpucu: tahmini zor, sana kolay bir cümle kullan — örn. üç kelime + rakam.
            </p>
          </>)}

          {mode === "forgot" && (
            <p style={{ fontSize: 12, color: C.text2, margin: 0, lineHeight: 1.6 }}>
              Hesabına kayıtlı e-posta adresini yaz — sana 1 saat geçerli bir sıfırlama bağlantısı gönderelim.
            </p>
          )}

          {error && (
            <div style={{ fontSize: 13, color: C.danger, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 12px" }}>
              {error}
            </div>
          )}
          {basari && (
            <div style={{ fontSize: 13, color: "#027a48", background: "#ecfdf3", border: "1px solid #abefc6", borderRadius: 8, padding: "8px 12px", lineHeight: 1.5 }}>
              {basari}
            </div>
          )}

          <button type="submit" disabled={busy}
            style={{
              marginTop: 4, padding: "11px 0", borderRadius: 10, border: "none", cursor: busy ? "wait" : "pointer",
              background: `linear-gradient(135deg, ${C.purple}, ${C.blue})`,
              color: "#fff", fontSize: 14, fontWeight: 600, opacity: busy ? 0.7 : 1,
            }}>
            {busy ? "Bekleyin…"
              : mode === "login" ? "Giriş Yap"
              : mode === "register" ? "Başvuruyu Gönder"
              : mode === "forgot" ? "Sıfırlama Bağlantısı Gönder"
              : "Parolayı Güncelle"}
          </button>

          {mode === "login" && (
            <button type="button"
              style={{ fontSize: 12, textDecoration: "underline", color: C.text2, background: "none", border: "none", cursor: "pointer", margin: "8px auto 0", display: "block" }}
              onClick={() => gecis("forgot")}>
              Şifremi unuttum
            </button>
          )}
          {(mode === "forgot" || mode === "reset") && (
            <button type="button"
              style={{ fontSize: 12, textDecoration: "underline", color: C.text2, background: "none", border: "none", cursor: "pointer", margin: "8px auto 0", display: "block" }}
              onClick={() => gecis("login")}>
              ← Girişe dön
            </button>
          )}
        </form>

        {mode === "register" && (
          <p style={{ fontSize: 11, color: C.text2, marginTop: 16, textAlign: "center", lineHeight: 1.5 }}>
            Başvurular güvenlik gereği yönetici onayından geçer — onaylanınca e-posta ile haber veririz.
          </p>
        )}
      </div>
      {/* Alt bilgiler: kartla aynı genişlikte, kartın ALTINDA */}
      <div style={{ width: "100%", maxWidth: 400 }}>
        {signupOpen === false && mode === "login" && (
          <p style={{ textAlign: "center", fontSize: 12, color: "#70706c", marginTop: 18, lineHeight: 1.6 }}>
            Geolop şu anda davetle çalışıyor. Hesabın yoksa{" "}
            <a href="mailto:info@geolop.com" style={{ color: "#ec6461" }}>info@geolop.com</a> adresinden bize ulaş.
          </p>
        )}
        <p style={{ textAlign: "center", fontSize: 12, color: "#a3a3a0", marginTop: 10, lineHeight: 1.6 }}>
          Giriş yaparak <a href="/kosullar" style={{ color: "#ec6461" }}>Kullanım Koşulları</a>'nı ve{" "}
          <a href="/gizlilik" style={{ color: "#ec6461" }}>Gizlilik / KVKK Aydınlatma Metni</a>'ni kabul etmiş olursun.
        </p>
      </div>
</div>
  );
}
